<template>
  <tr class="text-base dark:border-dark-5">
    <td
      :class="[
        'max-w-sm text-ellipsis whitespace-nowrap px-6 py-4',
        { 'pb-6': isLast },
      ]"
    >
      <BaseChip
        v-if="entity?.failedInstallationAttempt"
        :icon="NOT_MOUNTED_ICON"
        type="danger"
      />
      <FunkcheckIcon :room-id="entity.roomId" :device="device" />
      <span v-if="showFunkcheckStatus" class="mr-1 inline-flex align-middle">
        <span
          v-if="deviceFunkcheckStatus === Dmgw6DeviceFunkcheckStatus.pending"
          class="mdi mdi-loading mdi-spin text-gray-400 text-lg"
          data-testid="dmgw6-status-pending"
        />
        <span
          v-else-if="
            deviceFunkcheckStatus === Dmgw6DeviceFunkcheckStatus.partial
          "
          class="mdi mdi-check-circle text-yellow-400 text-lg"
          data-testid="dmgw6-status-partial"
        />
        <span
          v-else-if="
            deviceFunkcheckStatus === Dmgw6DeviceFunkcheckStatus.success
          "
          class="mdi mdi-check-circle text-green-500 text-lg"
          data-testid="dmgw6-status-success"
        />
        <span
          v-else-if="deviceFunkcheckStatus === Dmgw6DeviceFunkcheckStatus.error"
          class="mdi mdi-close-circle text-red-500 text-lg"
          data-testid="dmgw6-status-error"
        />
        <span
          v-else-if="
            deviceFunkcheckStatus === Dmgw6DeviceFunkcheckStatus.disconnected
          "
          class="mdi mdi-bluetooth-off text-gray-400 text-lg"
          data-testid="dmgw6-status-disconnected"
        />
        <span
          v-else
          class="mdi mdi-minus-circle-outline text-gray-300 text-lg"
          data-testid="dmgw6-status-unchecked"
        />
      </span>
      {{ isPlant ? device.note : device.serialNumber }}
    </td>
    <td :class="['py-4', { 'pb-6': isLast }]">
      <em
        v-if="device.customizedNumber === '42000'"
        class="mdi mdi-circle text-red-500 mr-1"
      />{{ device.customizedNumber }}
    </td>
    <td
      :class="['px-6 py-4', { 'pb-6': isLast }]"
      class="relative flex justify-end px-3"
    >
      <OptionsDropdown
        v-if="canShowThreeDotMenu"
        icon="mdi-dots-vertical"
        direction-x="left"
        direction="down"
        :test-id="`${
          isPlant ? device.note : props.device.serialNumber
        }_optionsDropdown`"
      >
        <li
          v-if="showTkdServiceReportButton"
          data-testid="tkdServiceReportButton"
          @click="onTkdService(entity, true)"
        >
          <em class="mdi mdi-file-document mr-2" />Service Report
        </li>
        <li
          v-if="showTkdServiceButton"
          data-testid="tkdServiceButton"
          @click="onTkdService(entity)"
        >
          <em class="mdi mdi-swap-horizontal mr-2" />TKD Service
        </li>
        <li
          v-if="useOrderStore().isTkdOrder() && !isPlant && !isSrtDevice"
          @click="onUnmount(entity)"
        >
          <button
            class="mdi mdi-arrow-u-left-bottom mr-2"
            data-testid="unmountDeviceButton"
          />
          Demontieren
        </li>
        <li
          :class="[
            {
              'cursor-not-allowed text-light-disabled-text hover:bg-transparent dark:text-dark-disabled-text':
                !showEdit,
            },
          ]"
          @click="showEdit ? onEdit(entity) : showDisabledNotification()"
        >
          <button class="mdi mdi-pencil mr-2" data-testid="editDeviceButton" />
          Bearbeiten
        </li>
        <li
          :class="[
            {
              'cursor-not-allowed text-light-disabled-text hover:bg-transparent dark:text-dark-disabled-text':
                !showDelete,
            },
          ]"
          @click="showDelete ? onDelete(entity) : showDisabledNotification()"
        >
          <button
            class="mdi mdi-delete mr-2"
            data-testid="deleteDeviceButton"
            :disabled="!showDelete"
          />
          Löschen
        </li>
      </OptionsDropdown>
    </td>
  </tr>
</template>

<script lang="ts" setup>
import FunkcheckIcon from '@/components/Funkcheck/FunkcheckIcon.vue';
import { DeviceClasses } from '@/models/devices/Device';
import { computed, ComputedRef } from 'vue';
import {
  Dmgw6DeviceFunkcheckStatus,
  useDmgw6Store,
} from '@/store/dmgw6/dmgw6Store';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import OptionsDropdown from '@/components/UI/Dropdown/OptionsDropdown.vue';
import { PlantDataClasses } from '@/models/plant/Plant';
import { entityTypes } from '@/enums/generic';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { NOT_MOUNTED_ICON } from '@/enums/icons';
import BaseChip from '@/components/UI/Chip/BaseChip.vue';
import { useOrderStore } from '@/store/order/orderStore';
import { InspectionPoint } from '@/models/inspectionPoint/InspectionPoint';
import { useInspectionPointStore } from '@/store/entities/inspectionPointStore';
import { InspectionDeviceClasses } from '@/models/inspectionPoint/InspectionDevice';
import { deviceTypes } from '@/enums/device';
import { EntityTransactionType } from '@/models/OrderChanges';
import { useNotificationStore } from '@/store/notifications/notificationStore';
import { Notification } from '@/models/Notification';

const entity: ComputedRef<
  InstallationPoint | PlantDataClasses | InspectionPoint | undefined //TODO: should never be undefined
> = computed(() => {
  if (props.device.type === entityTypes.plant) {
    return props.device as PlantDataClasses;
  }

  const inspectionPoint = useInspectionPointStore().inspectionPoints.get(
    props.device.inspectionPointId
  );
  if (useOrderStore().isOnSiteInspection() && inspectionPoint) {
    return useInspectionPointStore().inspectionPoints.get(
      props.device.inspectionPointId
    ) as InspectionPoint;
  }

  const installationPoint = useInstallationPointStore().installationPoints.get(
    props.device.installationPointId
  );
  return installationPoint as InstallationPoint;
});

const props = defineProps<{
  device: DeviceClasses | PlantDataClasses | InspectionDeviceClasses;
  onEdit: (arg0: InstallationPoint | PlantDataClasses) => void;
  onUnmount?: (arg0: InstallationPoint) => void;
  onDelete?: (arg0: InstallationPoint | PlantDataClasses) => void;
  onExchange: () => void;
  onTkdService: () => void;
  isLast: boolean;
  isPlant: boolean;
  showFunkcheckStatus?: boolean;
}>();

const deviceFunkcheckStatus = computed(
  () => useDmgw6Store().deviceFunkcheckStatuses.get(props.device.id) ?? null
);

const showEdit = computed(() => {
  if (props.isPlant) {
    const id = entity.value?.id;
    if (!id) return false;
    return useOrderStore().hasTransaction(
      id,
      EntityTransactionType.CREATE_ENTITY
    );
  }
  const installationId = props.device.installationPointId;
  const entityId = installationId ?? entity.value?.id;
  if (!entityId) return false;
  return useOrderStore().hasTransaction(
    entityId,
    EntityTransactionType.CREATE_ENTITY
  );
});

const showDelete = computed(() => {
  if (props.isPlant) {
    const id = entity.value?.id;
    if (!id) return false;
    const hasNoDevices = useDeviceStore().getDevicesOnPlant(id).length === 0;
    return showEdit.value && hasNoDevices;
  }
  return showEdit.value;
});

const showDisabledNotification = () => {
  useNotificationStore().addNotification(
    new Notification(
      'Dieses Element wurde in einem früheren Auftrag erstellt und kann hier nicht bearbeitet werden.',
      '',
      'info'
    )
  );
};

// Kept for backwards compatibility
const isPlantDeletable = computed(() => {
  if (entity.value.type === entityTypes.plant) {
    return useDeviceStore().getDevicesOnPlant(entity.value.id).length === 0;
  }
  return true;
});

const showTkdServiceReportButton = computed(() => {
  return (
    isTkdOrder.value &&
    !props.isPlant &&
    isSrtDevice.value &&
    hasTKDService.value
  );
});

const showTkdServiceButton = computed(() => {
  return (
    isTkdOrder.value &&
    !props.isPlant &&
    isSrtDevice.value &&
    !hasTKDService.value
  );
});

const hasTKDService = computed(() => {
  if (!entity.value) return false;
  return useOrderStore().hasTKDService(entity.value.id);
});

const isTkdOrder = computed(() => {
  return useOrderStore().isTkdOrder();
});

const isSrtDevice = computed(() => {
  if (props.device.deviceType) {
    return props.device.deviceType === deviceTypes.SmartRadiatorThermostat;
  }
  return false;
});

const canShowThreeDotMenu = computed(() => {
  return props.device?.deviceType !== deviceTypes.TemperatureSensor;
});
</script>
